import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { Tag } from 'antd'
import formatNumber from '../../helpers/formatters/format-any-number'
import renderDate from '../../helpers/tables/render-date'
import PrintJob from '../../types/api/print-job'
import ShipmentPosition from '../../types/api/shipment-position'
import formatMachineBrands from '../../helpers/calculations/formatters/format-machine-brands'
import PrintJobExecutor from '../../types/api/print-job-executor'
import renderDatetime from '../../helpers/tables/render-datetime'
import { MaterialRequirement } from '../../types/api/components/material/material-requirements'
import { MaterialColumnCell } from '../schedules/flexo/components/cells/material-column-cell'
import formatDuration from '../../helpers/date-time/format-duration'
import { formatRollerDisplay } from '../../helpers/format-roller-display'
import Calculated from '../../types/api/components/calculation-position/calculated'

function formatMaterialMetrics(calculated: Calculated | undefined | null): string {
    if (!calculated) return '—'

    const length =
        calculated.materialLength == null
            ? null
            : Math.round(calculated.materialLength * 1000) / 1000
    const width = calculated.materialWidth

    if (length == null && width == null) return '—'

    const parts: string[] = []
    if (width != null) parts.push(`Ш ${width}`)
    if (length != null) parts.push(`Д ${length}`)
    return parts.join(' / ')
}

function getExecutorsTimeBounds(executors: PrintJobExecutor[] | null | undefined) {
    const starts = (executors || [])
        .map(executor => executor.start)
        .filter((value): value is string => Boolean(value))
        .map(value => dayjs(value))
        .filter(value => value.isValid())

    const ends = (executors || [])
        .map(executor => executor.end)
        .filter((value): value is string => Boolean(value))
        .map(value => dayjs(value))
        .filter(value => value.isValid())

    if (starts.length === 0) {
        return { start: null, end: null, durationMinutes: null }
    }

    const start = starts.reduce((min, current) => (current.isBefore(min) ? current : min))
    const end =
        ends.length === 0
            ? null
            : ends.reduce((max, current) => (current.isAfter(max) ? current : max))

    const durationMinutes =
        end == null ? null : Math.max(0, end.diff(start, 'minute', true))

    return { start, end, durationMinutes }
}


export const columns: ColumnsType<PrintJob> = [
    {
        dataIndex: 'id',
        title: 'Номер',
        width: 60,
    },
    {
        dataIndex: ['shipment', 'clientDeliveryDate'],
        title: 'Дата поставки',
        width: 70,
        render: v => (v ? renderDate(v) : '—'),
    },
    {
        dataIndex: ['shipment', 'client', 'name'],
        title: 'Клиент',
    },
    {
        dataIndex: ['shipment', 'shipment_positions'],
        title: 'Тираж',
        render(v: ShipmentPosition[] | null) {
            return formatNumber((v ?? []).reduce((acc, v) => acc + v.copies, 0))
        },
    },
    {
        dataIndex: ['shipment', 'materialRequirements'],
        title: 'Материал',
        width: 100,
        render(v: MaterialRequirement[] | null, record: PrintJob) {
            return <MaterialColumnCell value={v ?? []} shipment={record.shipment} />
        },
    },
    {
        title: 'Материал расч.',
        width: 100,
        render(_v, r: PrintJob) {
            return formatMaterialMetrics(r.shipment?.calculated)
        },
    },
    {
        title: 'Материал факт.',
        width: 100,
        render(_v, r: PrintJob) {
            return formatMaterialMetrics(r.shipment?.calculatedOnActual)
        },
    },
    {
        dataIndex: ['shipment', 'calculated', 'materialLength'],
        title: 'Метраж расч.',
        width: 70,
        render(_v, r: PrintJob) {
            const length = r.shipment?.calculated?.materialLength
            return length == null ? '—' : Math.round(length * 1000) / 1000
        },
    },
    {
        dataIndex: ['shipment', 'calculatedOnActual', 'materialLength'],
        title: 'Метраж факт.',
        width: 70,
        render(_v, r: PrintJob) {
            const length = r.shipment?.calculatedOnActual?.materialLength
            return length == null ? '—' : Math.round(length * 1000) / 1000
        },
    },
    {
        dataIndex: ['shipment', 'stamp'],
        title: 'Номер',
        width: 100,
        render(v) {
            return `${v?.number || 'нет'}`
        },
    },
    {
        dataIndex: ['shipment', 'stamp', 'roller', 'roller'],
        title: 'Вал',
        render: (v: number) => formatRollerDisplay(v),
    },
    {
        dataIndex: ['shipment', 'calculated', 'commonTime'],
        title: 'Время тиража',
        render(_v, r: PrintJob) {
            const time = r.shipment?.calculatedOnActual?.commonTime ?? r.shipment?.calculated?.commonTime
            return time == null ? '—' : formatDuration(time)
        },
    },
    {
        title: 'Факт. время',
        width: 80,
        render(_v, r: PrintJob) {
            const { durationMinutes } = getExecutorsTimeBounds(r.print_job_executors)
            return durationMinutes == null ? '—' : formatDuration(durationMinutes)
        },
    },
    {
        dataIndex: 'machine',
        title: 'Приор. станок',
        render(v, r) {
            return r.shipment ? formatMachineBrands(r.shipment) || '—' : '—'
        },
    },
    {
        dataIndex: ['print_job_executors'],
        title: 'Начало',
        render(v: PrintJobExecutor[] | null) {
            const { start } = getExecutorsTimeBounds(v)
            return start ? renderDatetime(start.toISOString()) : null
        },
    },
    {
        dataIndex: ['print_job_executors'],
        title: 'Окончание',
        render(v: PrintJobExecutor[] | null) {
            const { end } = getExecutorsTimeBounds(v)
            return end ? renderDatetime(end.toISOString()) : null
        },
    },
    {
        dataIndex: ['jobStatus'],
        title: 'Статус',
        width: 100,
        render(v) {
            return <Tag color={'green'}>{v}</Tag>
        },
    },
]
